import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';

/**
 * Card shell for the Management Task lists, matching the Project card in
 * ProjectsList.jsx — these grids sit on the same page, so they should be
 * indistinguishable in style.
 *
 * Common parts only: shell, initials tile, title, optional subtitle, optional
 * edit button. Each list passes its own meta rows as children, because what
 * matters on a task differs from what matters on a budget or a permission.
 */
export const chipClass = {
    positive: 'bg-[#EAF8F2] text-[#0F766E]',
    neutral: 'bg-[#F3F4F6] text-[#6B7280]',
    indigo: 'bg-[#EEF2FF] text-[#4F46E5]',
    amber: 'bg-[#FFF4E8] text-[#B45309]',
    purple: 'bg-[#F3E8FF] text-[#7E22CE]',
    rose: 'bg-[#FEE9EC] text-[#B42318]',
    sky: 'bg-[#E0F2FE] text-[#0369A1]',
    teal: 'bg-[#E6F1F2] text-[#2A565E]'
};

export const Chip = ({ tone = 'neutral', children, className = '' }) => (
    <span
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold ${chipClass[tone] || chipClass.neutral} ${className}`}>
        {children}
    </span>
);

const initialsOf = (text, fallback) =>
    (text || fallback)
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('') || fallback;

const cornerButtonClass =
    'flex h-8 w-8 items-center justify-center rounded-full border border-[#E5EAF1] bg-white shadow-sm transition-colors';

// The card itself opens on Enter/Space, so a corner button stops its keydown
// from reaching the card — otherwise the key opens the task instead.
const CornerButton = ({ label, onClick, className, children }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        className={`${cornerButtonClass} ${className}`}>
        {children}
    </button>
);

const TaskCard = ({
    title,
    subtitle,
    fallbackInitials = 'TK',
    onOpen,
    onEdit,
    editLabel = 'Edit task',
    onDelete,
    deleteLabel = 'Delete task',
    children
}) => (
    <article
        role="link"
        tabIndex={0}
        aria-label={`Open ${title || 'task'}`}
        onClick={onOpen}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen?.();
            }
        }}
        className="group bg-white rounded-2xl border border-[#E5EAF1] p-4 flex flex-col gap-2.5 shadow-sm relative cursor-pointer min-h-[220px] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2">
        {(onEdit || onDelete) && (
            <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                {onEdit && (
                    <CornerButton
                        label={editLabel}
                        onClick={onEdit}
                        className="text-[#4D6C72] hover:bg-[#F7FAFB]">
                        <Pencil size={16} strokeWidth={2} />
                    </CornerButton>
                )}
                {onDelete && (
                    <CornerButton
                        label={deleteLabel}
                        onClick={onDelete}
                        className="text-[#B42318] hover:bg-[#FEE9EC]">
                        <Trash2 size={16} strokeWidth={2} />
                    </CornerButton>
                )}
            </div>
        )}

        <div className="flex-1 flex flex-col gap-2.5 transition-colors duration-300 rounded-xl group-hover:bg-[#FAFBFC]/80">
            <div
                className={`flex items-start gap-3 ${
                    onEdit && onDelete ? 'pr-20' : onEdit || onDelete ? 'pr-12' : ''
                }`}>
                <div className="h-14 w-14 shrink-0 rounded-xl border border-[#BFE6DE] bg-[#E7F2EF] text-[#2A565E] flex items-center justify-center text-lg font-semibold">
                    {initialsOf(title, fallbackInitials)}
                </div>
                <div className="min-w-0">
                    <p
                        className="text-[20px] leading-tight font-semibold text-[#111827] truncate"
                        title={title || '---'}>
                        {title || '---'}
                    </p>
                    {subtitle}
                </div>
            </div>
            {children}
        </div>
    </article>
);

export default TaskCard;
